import React, { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { FilledTextFieldProps } from '@mui/material/TextField';
import { Box, Dialog, DialogContent, DialogTitle, InputAdornment } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownLeftAndArrowUpRightToCenter, faArrowUpRightAndArrowDownLeftFromCenter, faMicrochipAi } from '@fortawesome/pro-light-svg-icons';
import { ITextFieldProps } from './text-field-props';
import { TextField } from './text-field';
import { ColorPalette } from '../config';
import { Cell } from '../cell';
import { Row } from '../row';

export interface IMaxLengthTextFieldProps extends ITextFieldProps {
  maxLength?: number;
  maxLengthError?: string;
  lazy?: boolean;
  showAiIcon?: boolean;
  forceMaxLength?: boolean;
  useJsonStringifyCount?: boolean; // Use this prop to count the length of the stringified JSON string instead of the plain text.
  disableMaxLengthError?: boolean; // If true, the maxLength error will not be displayed even if the length exceeds maxLength.
  maximizable?: boolean;
  hideCharacterCount?: boolean; // Use this prop on a field with no length restriction, where a running count only confuses.
}

const useStyles = makeStyles()(() =>
  ({
    wrapper: {
      display: 'flex',
      '& span:last-child': {
        marginLeft: 'auto',
      },
    },
  })
);

export function getJsonStringifyLength(str: string | undefined): number {
  if (!str) return 0;
  return JSON.stringify(str).length - 2; // -2 to remove start and end quotes from stringified JSON
}

/*
 * A specialized textbox that displays current length and maximum number of characters as helperText.
 * Note! Textbox is *not* limited to maxLength (allowing paste of long text), but the textbox enters error state.
 */
export function MaxLengthTextField(props: React.PropsWithChildren<IMaxLengthTextFieldProps>) {
  const { maximizable = false, ...rest } = props;

  return maximizable ? <MaximizableMaxLengthTextField {...rest} /> : <InnerMaxLengthTextField {...rest} />;
}

function MaximizableMaxLengthTextField(props: React.PropsWithChildren<IMaxLengthTextFieldProps>) {
  const { InputProps, rows, maxRows, ...rest } = props;
  const [isMaximized, setIsMaximized] = useState<boolean>(false);

  return (
    <>
      <InnerMaxLengthTextField
        rows={rows}
        maxRows={maxRows}
        sx={{
          '& .MuiInputBase-root': {
            position: 'relative',
          },
          '& .MuiInputAdornment-root': {
            maxHeight: 'none',
            margin: 0,
          },
        }}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <InputAdornment
              component="span"
              position="end"
              sx={{
                position: 'absolute',
                cursor: 'pointer',
                alignSelf: 'end',
                right: '4px',
                bottom: '4px',
              }}
              onClick={() => setIsMaximized(true)}
            >
              <Box sx={{ height: '100%', display: 'flex', p: 1 }}>
                <FontAwesomeIcon icon={faArrowUpRightAndArrowDownLeftFromCenter} />
              </Box>
            </InputAdornment>
          ),
        }}
        {...rest}
      />
      <MaximizableMaxLengthTextFieldDialog onMinimize={() => setIsMaximized(false)} isOpen={isMaximized} rows={20} {...rest} />
    </>
  );
}

interface IMaximizableMaxLengthTextFieldDialogProps extends IMaxLengthTextFieldProps {
  isOpen: boolean;
  onMinimize: () => void;
}

function MaximizableMaxLengthTextFieldDialog(props: IMaximizableMaxLengthTextFieldDialogProps) {
  const { isOpen, rows, onMinimize, ...rest } = props;
  return (
    <Dialog open={isOpen} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box display="flex" justifyContent="end">
          <FontAwesomeIcon icon={faArrowDownLeftAndArrowUpRightToCenter} style={{ cursor: 'pointer' }} onClick={onMinimize} size="xs" />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Row>
          <Cell size="full">
            <MaxLengthTextField rows={rows} maxRows={35} {...rest} />
          </Cell>
        </Row>
      </DialogContent>
    </Dialog>
  );
}

function InnerMaxLengthTextField(props: React.PropsWithChildren<IMaxLengthTextFieldProps>) {
  const {
    maxLength,
    maxLengthError,
    helperText,
    label,
    showAiIcon,
    onChange,
    onBlur,
    lazy,
    useJsonStringifyCount,
    disableMaxLengthError,
    value,
    error,
    errorText,
    forceMaxLength,
    hideCharacterCount,
    children,
    ...rest
  } = props;
  const { classes } = useStyles();
  const [stateValue, setStateValue] = useState(value);

  useEffect(() => {
    if (lazy) {
      setStateValue(value);
    }
  }, [value]);

  function getLength() {
    if (useJsonStringifyCount) {
      if (lazy) {
        return getJsonStringifyLength(stateValue as string);
      }
      return getJsonStringifyLength(value as string);
    }
    if (lazy) {
      return stateValue ? (stateValue as string).length : 0;
    }
    return value ? (value as string).length : 0;
  }

  const length = getLength();
  const changedProps = {} as Partial<ITextFieldProps>;

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (lazy) {
      setStateValue(event.target.value);
    } else {
      onChange && onChange(event);
    }
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) {
    lazy && onChange && onChange(event);
    onBlur && onBlur(event);
  }

  if (maxLength !== undefined && length > maxLength && !error && !disableMaxLengthError) {
    changedProps.error = true;
    changedProps.errorText = `${maxLengthError || 'Teksten indeholder for mange tegn'} (${length}/${maxLength})`;
  }

  const helperTextAlignment = changedProps.error || error || helperText ? 'left' : 'right';

  const FormHelperTextProps: FilledTextFieldProps['FormHelperTextProps'] = {
    style: { textAlign: helperTextAlignment },
  };

  return (
    <TextField
      {...rest}
      {...changedProps}
      helperText={
        hideCharacterCount ? (
          helperText
        ) : (
          <span className={classes.wrapper}>
            <span>{helperText}</span>
            <span>{maxLength !== undefined ? `${length}/${maxLength}` : length}</span>
          </span>
        )
      }
      FormHelperTextProps={FormHelperTextProps}
      onChange={handleChange}
      onBlur={handleBlur}
      lazy={false}
      value={lazy ? stateValue : value}
      maxLength={forceMaxLength && maxLength !== undefined ? maxLength : undefined}
      label={
        showAiIcon ? (
          <Box display="flex" gap={0.5}>
            <Box>{label}</Box>
            <FontAwesomeIcon size="lg" icon={faMicrochipAi} color={ColorPalette.HotAurora} title="Dette felt kan indeholde tekst, der er baseret på AI." />
          </Box>
        ) : (
          label
        )
      }
      error={error || changedProps.error}
      errorText={errorText || changedProps.errorText}
    >
      {children}
    </TextField>
  );
}
